// src/pages/admin/AdminUsersPage.tsx - Superuser account management: disable/enable,
// promote/demote superuser, and delete (guarded by a type-the-username confirm).
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { AdminUser } from '../../types';
import { timeAgo } from '../../utils/timeAgo';
import BackLink from '../../components/common/BackLink';

function StatusBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`px-1.5 py-0.5 text-[0.65rem] font-display uppercase tracking-wide leading-none ${
        active ? 'bg-success text-white' : 'bg-danger text-white'
      }`}
    >
      {active ? 'Active' : 'Disabled'}
    </span>
  );
}

function AdminBadge() {
  return (
    <span className="px-1.5 py-0.5 text-[0.65rem] font-display uppercase tracking-wide leading-none bg-ink-900 text-gold">
      Admin
    </span>
  );
}

type ConfirmKind = 'active' | 'superuser';

export default function AdminUsersPage() {
  const { user } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmRow, setConfirmRow] = useState<{ id: number; kind: ConfirmKind } | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refetch = useCallback(async () => setUsers(await apiService.getAdminUsers()), []);

  useEffect(() => {
    if (!user?.is_superuser) return;
    (async () => {
      setLoading(true);
      setPageError(null);
      try {
        await refetch();
      } catch (e: any) {
        setPageError(e.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.is_superuser, refetch]);

  if (!user?.is_superuser) {
    return <Navigate to="/app" replace />;
  }

  const toggleActive = async (u: AdminUser) => {
    setActionError(null);
    setBusyId(u.id);
    try {
      await apiService.setUserActive(u.id, !u.is_active);
      await refetch();
    } catch (e: any) {
      setActionError(e.message || 'Action failed');
    } finally {
      setBusyId(null);
      setConfirmRow(null);
    }
  };

  const toggleSuperuser = async (u: AdminUser) => {
    setActionError(null);
    setBusyId(u.id);
    try {
      await apiService.setUserSuperuser(u.id, !u.is_superuser);
      await refetch();
    } catch (e: any) {
      setActionError(e.message || 'Action failed');
    } finally {
      setBusyId(null);
      setConfirmRow(null);
    }
  };

  const openDelete = (u: AdminUser) => {
    setActionError(null);
    setDeleteError(null);
    setDeleteConfirmText('');
    setDeleteTarget(u);
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiService.deleteUser(deleteTarget.id);
      closeDelete();
      await refetch();
    } catch (e: any) {
      setDeleteError(e.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <BackLink to="/app/admin">Back to Admin Panel</BackLink>
      <div className="border-b-2 border-ink-900 pb-4 mt-4 mb-8">
        <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Users</h1>
        <p className="text-secondary-600 mt-1">Accounts, access, and cleanup.</p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="loading-spinner w-6 h-6 mr-3" />
          <span className="text-secondary-500 text-sm">Loading users…</span>
        </div>
      )}

      {!loading && pageError && <p className="text-sm text-danger py-4">{pageError}</p>}

      {!loading && !pageError && (
        <>
          {actionError && <p className="text-sm text-danger mb-4">{actionError}</p>}

          {users.length === 0 ? (
            <p className="py-12 text-center font-display uppercase tracking-wider text-secondary-400">
              No users.
            </p>
          ) : (
            <div className="overflow-x-auto bg-white border border-secondary-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-secondary-500 uppercase tracking-wide text-xs border-b-2 border-ink-900">
                    <th className="py-2.5 px-4 text-left font-semibold">User</th>
                    <th className="py-2.5 px-4 text-left font-semibold">Status</th>
                    <th className="py-2.5 px-4 text-left font-semibold">Created</th>
                    <th className="py-2.5 px-4 text-left font-semibold">Last seen</th>
                    <th className="py-2.5 px-4 text-right font-semibold">Leagues</th>
                    <th className="py-2.5 px-4 text-right font-semibold">Teams</th>
                    <th className="py-2.5 px-4 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-200">
                  {users.map((u) => {
                    const rowBusy = busyId === u.id;
                    const confirming = confirmRow?.id === u.id ? confirmRow.kind : null;
                    return (
                      <tr key={u.id} className="text-ink-900">
                        <td className="py-3 px-4">
                          <div className="font-medium">{u.username}</div>
                          <div className="text-xs text-secondary-500">{u.email}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            <StatusBadge active={u.is_active} />
                            {u.is_superuser && <AdminBadge />}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-secondary-500 text-xs">
                          {timeAgo(u.created_at)}
                        </td>
                        <td className="py-3 px-4 text-xs" data-testid="last-seen">
                          {u.last_login_at
                            ? <span className="text-ink-900">{timeAgo(u.last_login_at)}</span>
                            : <span className="text-secondary-400">never</span>}
                        </td>
                        <td className="py-3 px-4 text-right font-display">{u.leagues_owned}</td>
                        <td className="py-3 px-4 text-right font-display">{u.teams}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            {confirming === 'active' ? (
                              <>
                                <span className="text-xs text-secondary-500">
                                  {u.is_active ? 'Disable this user?' : 'Enable this user?'}
                                </span>
                                <button
                                  type="button"
                                  disabled={rowBusy}
                                  onClick={() => toggleActive(u)}
                                  className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider bg-ink-900 text-paper hover:bg-ink-900/90 disabled:opacity-50"
                                >
                                  {rowBusy ? 'Working…' : `Confirm ${u.is_active ? 'Disable' : 'Enable'}`}
                                </button>
                                <button
                                  type="button"
                                  disabled={rowBusy}
                                  onClick={() => setConfirmRow(null)}
                                  className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-secondary-300 text-secondary-600 hover:text-ink-900 disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : confirming === 'superuser' ? (
                              <>
                                <span className="text-xs text-secondary-500">
                                  {u.is_superuser ? 'Demote this user?' : 'Promote this user?'}
                                </span>
                                <button
                                  type="button"
                                  disabled={rowBusy}
                                  onClick={() => toggleSuperuser(u)}
                                  className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider bg-ink-900 text-paper hover:bg-ink-900/90 disabled:opacity-50"
                                >
                                  {rowBusy ? 'Working…' : `Confirm ${u.is_superuser ? 'Demote' : 'Promote'}`}
                                </button>
                                <button
                                  type="button"
                                  disabled={rowBusy}
                                  onClick={() => setConfirmRow(null)}
                                  className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-secondary-300 text-secondary-600 hover:text-ink-900 disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  aria-label={`${u.is_active ? 'Disable' : 'Enable'} ${u.username}`}
                                  disabled={rowBusy}
                                  onClick={() => setConfirmRow({ id: u.id, kind: 'active' })}
                                  className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-paper disabled:opacity-50"
                                >
                                  {u.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                  type="button"
                                  aria-label={`${u.is_superuser ? 'Demote' : 'Promote'} ${u.username}`}
                                  disabled={rowBusy}
                                  onClick={() => setConfirmRow({ id: u.id, kind: 'superuser' })}
                                  className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-ink-900 text-ink-900 hover:bg-ink-900 hover:text-paper disabled:opacity-50"
                                >
                                  {u.is_superuser ? 'Demote' : 'Promote'}
                                </button>
                                <button
                                  type="button"
                                  aria-label={`Delete ${u.username}`}
                                  disabled={rowBusy}
                                  onClick={() => openDelete(u)}
                                  className="btn px-2.5 py-1 text-xs font-semibold uppercase tracking-wider border border-danger text-danger hover:bg-danger hover:text-white disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4">
          <div
            className="w-full max-w-md border border-secondary-300 bg-white p-6 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-user-heading"
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !deleting) {
                closeDelete();
              }
            }}
          >
            <h3
              id="delete-user-heading"
              className="font-display uppercase text-xl leading-none text-danger-700"
            >
              Delete User
            </h3>
            <p className="mt-3 text-sm text-secondary-700">
              This permanently removes the account. Type the username{' '}
              <span className="font-semibold text-ink-900">{deleteTarget.username}</span> to confirm.
            </p>
            <input
              type="text"
              aria-label="Type the username to confirm"
              autoFocus
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="form-input mt-4 w-full"
            />
            {deleteError && (
              <p className="mt-3 text-sm text-danger-600" role="alert">
                {deleteError}
              </p>
            )}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                className="btn-outline-secondary flex-1"
                onClick={closeDelete}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger flex-1"
                onClick={confirmDelete}
                disabled={deleting || deleteConfirmText !== deleteTarget.username}
              >
                {deleting ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
